/**
 * Day-key arithmetic in the application timezone (spec §4.6, §4.7).
 * A "day key" is the local calendar date as `YYYY-MM-DD`.
 * All timestamps are epoch milliseconds UTC.
 */

export const DEFAULT_TIMEZONE = "Europe/Stockholm";

const DAY_MS = 86_400_000;

const formatterCache = new Map<string, Intl.DateTimeFormat>();
function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatterCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    formatterCache.set(tz, f);
  }
  return f;
}

interface Wall {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function wallTime(ms: number, tz: string): Wall {
  const parts = formatter(tz).formatToParts(new Date(ms));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? "0");
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
}

/** Offset of `tz` at instant `ms`, in ms (positive east of UTC). */
export function timezoneOffsetMs(ms: number, tz: string): number {
  const w = wallTime(ms, tz);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second, 0);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

const DAY_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Local calendar date of an instant, as `YYYY-MM-DD`. */
export function dayKey(ms: number, tz: string = DEFAULT_TIMEZONE): string {
  const w = wallTime(ms, tz);
  const p = (n: number, len = 2) => String(n).padStart(len, "0");
  return `${p(w.year, 4)}-${p(w.month)}-${p(w.day)}`;
}

/**
 * Width of the `dayKeyCached` bucket. Since 2011 every IANA offset is a whole
 * number of 15 minutes, and every change of the local date (local midnight,
 * or a switch across it) lies on a 15-minute boundary of UTC, so all
 * instants of one bucket share their local date. The few switches off that
 * grid since then (Goose_Bay, St_Johns, Gaza, Hebron in 2011;
 * Antarctica/Casey 2020–2022) are 00:01 spring-forwards within one date.
 * Earlier exceptions: St_Johns, Goose_Bay and Moncton had 00:01 fall-backs
 * until 2010; Monrovia and Kiritimati had odd offsets before 1980.
 */
const DAY_KEY_BUCKET_MS = 15 * 60_000;
/** Buckets remembered per time zone before the memo starts over (a year is 35 040). */
const DAY_KEY_CACHE_LIMIT = 50_000;
const dayKeyCache = new Map<string, Map<number, string>>();

/**
 * `dayKey`, memoised per 15-minute UTC bucket and time zone (pedagogy spec
 * §3.3.1 #6). Evidence, mastery, streak and activity need the local day of
 * every attempt row; `Intl` formatting costs several microseconds per call,
 * the memo one map lookup. Equal to `dayKey` for every instant since 2011 in
 * every IANA zone; see `DAY_KEY_BUCKET_MS` for the pre-2011 exceptions.
 */
export function dayKeyCached(ms: number, tz: string = DEFAULT_TIMEZONE): string {
  return bucketDayKey(bucketsOf(tz), Math.floor(ms / DAY_KEY_BUCKET_MS), tz);
}

function bucketsOf(tz: string): Map<number, string> {
  let byBucket = dayKeyCache.get(tz);
  if (!byBucket) {
    byBucket = new Map();
    dayKeyCache.set(tz, byBucket);
  }
  return byBucket;
}

function bucketDayKey(byBucket: Map<number, string>, bucket: number, tz: string): string {
  let key = byBucket.get(bucket);
  if (key === undefined) {
    if (byBucket.size >= DAY_KEY_CACHE_LIMIT) byBucket.clear();
    key = dayKey(bucket * DAY_KEY_BUCKET_MS, tz);
    byBucket.set(bucket, key);
  }
  return key;
}

/**
 * `dayKeyCached` for one pass over many instants in one zone (the evidence
 * read's hot loops, pedagogy spec §3.3.1): it also remembers the previous
 * row's bucket, so a run of rows in one 15-minute bucket (a practice session,
 * rows ascending) skips even the memo lookup. Same result as `dayKeyCached`
 * for every instant, in any order.
 */
export function dayKeyCursor(tz: string = DEFAULT_TIMEZONE): (ms: number) => string {
  const byBucket = bucketsOf(tz);
  let lastBucket = Number.NaN;
  let lastKey = "";
  return (ms) => {
    const bucket = Math.floor(ms / DAY_KEY_BUCKET_MS);
    if (bucket !== lastBucket) {
      lastKey = bucketDayKey(byBucket, bucket, tz);
      lastBucket = bucket;
    }
    return lastKey;
  };
}

/**
 * The distinct local days of `instants` (any order), as `dayKeyCached` keys.
 * An instant in the previous instant's bucket, or on the day just added,
 * skips the memo and the set, so an ascending history costs about one
 * comparison per row. Streaks read it (§3.3.1).
 */
export function distinctDayKeys(
  instants: readonly number[],
  tz: string = DEFAULT_TIMEZONE,
): Set<string> {
  const byBucket = bucketsOf(tz);
  const days = new Set<string>();
  let lastBucket = Number.NaN;
  let previous: string | undefined;
  // An index loop: `for…of` over a number array boxes every element here.
  for (let i = 0; i < instants.length; i++) {
    const bucket = Math.floor(instants[i] / DAY_KEY_BUCKET_MS);
    if (bucket === lastBucket) continue;
    lastBucket = bucket;
    const key = bucketDayKey(byBucket, bucket, tz);
    if (key === previous) continue;
    days.add(key);
    previous = key;
  }
  return days;
}

/** Epoch ms of local midnight that starts the given day key. */
export function startOfDayMs(key: string, tz: string = DEFAULT_TIMEZONE): number {
  const m = DAY_KEY_RE.exec(key);
  if (!m) throw new Error(`invalid day key: ${key}`);
  const utcGuess = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
  // Two passes handle DST transitions at/near midnight.
  let ts = utcGuess - timezoneOffsetMs(utcGuess, tz);
  ts = utcGuess - timezoneOffsetMs(ts, tz);
  // If midnight does not exist locally (spring-forward at 00:00), step to the
  // first instant of that local day.
  if (dayKey(ts, tz) !== key) {
    for (let i = 1; i <= 4; i++) {
      const candidate = ts + i * 3_600_000;
      if (dayKey(candidate, tz) === key) return candidate;
    }
  }
  return ts;
}

/** Whole days from day key `a` to day key `b` (b − a); negative when b is earlier. */
export function daysBetween(a: string, b: string): number {
  const pa = DAY_KEY_RE.exec(a);
  const pb = DAY_KEY_RE.exec(b);
  if (!pa || !pb) throw new Error(`invalid day key: ${!pa ? a : b}`);
  const ua = Date.UTC(Number(pa[1]), Number(pa[2]) - 1, Number(pa[3]));
  const ub = Date.UTC(Number(pb[1]), Number(pb[2]) - 1, Number(pb[3]));
  return Math.round((ub - ua) / DAY_MS);
}

/** Whole days from 1970-01-01 to day key `key` (`daysBetween("1970-01-01", key)`, one parse). */
export function dayNumber(key: string): number {
  const m = DAY_KEY_RE.exec(key);
  if (!m) throw new Error(`invalid day key: ${key}`);
  return Math.round(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS);
}

/** Day key `n` days after `key` (n may be negative). */
export function addDays(key: string, n: number): string {
  const m = DAY_KEY_RE.exec(key);
  if (!m) throw new Error(`invalid day key: ${key}`);
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) + n * DAY_MS);
  const p = (x: number, len = 2) => String(x).padStart(len, "0");
  return `${p(d.getUTCFullYear(), 4)}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

/** Whole local days between two instants (calendar difference, not 24h blocks). */
export function daysAgo(ms: number, nowMs: number, tz: string = DEFAULT_TIMEZONE): number {
  return daysBetween(dayKeyCached(ms, tz), dayKeyCached(nowMs, tz));
}

export const MS_PER_DAY = DAY_MS;
